import React, { useEffect, useMemo, useRef, useState } from "react";
import InvoiceForm, {
  invoiceFormSchema,
  InvoiceFormValues,
} from "./invoice-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ApiResponse, Invoice } from "@/lib/types";
import { Step } from "./invoice-form/constants";
import { useGetVendors } from "@/services/queries/vendor";
import { api, BASE_URL, getActiveOrganizationId } from "@/lib/api";
import { toDateOnlyString } from "@/lib/utils";
import {
  useCreateInvoiceAttachment,
  useDeleteInvoiceAttachment,
  useUpdateInvoice,
} from "@/services/mutations/invoices";
import { toast } from "@/hooks/use-toast";
import useAuth from "@/store/use-auth";
import { useCreateVendor } from "@/services/mutations/vendor";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Mixpanel } from "@/lib/mixpanel";

const EditInvoiceForm = ({
  open,
  setOpen,
  invoice,
  refetch,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  invoice: Invoice;
  refetch: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [step, setStep] = useState<number>(Step.INITAL);
  const { data: vendorsData } = useGetVendors();
  const { mutateAsync: createInvoiceAttachment } = useCreateInvoiceAttachment();
  const { mutateAsync: deleteInvoiceAttachment } = useDeleteInvoiceAttachment();
  // Hard guard against double-submit: a synchronous ref blocks duplicate requests even when rapid taps fire before React re-renders. Cleared only when the whole flow settles.
  const isSubmittingRef = useRef(false);

  const { mutate: createVendor, isPending: isCreatingVendor } = useCreateVendor(
    {
      onSuccess: ({ value }) => {
        // Keep isSubmittingRef true — the invoice mutation's own handlers clear it.
        handleUpdateInvoice(form.getValues(), value.id);
        Mixpanel.track("Vendor Added When Editing Invoice");
      },
      onError: (error) => {
        console.error(error);
        Mixpanel.track("Vendor Addition Failed When Editing Invoice");
        isSubmittingRef.current = false;
      },
    }
  );

  const { mutate: updateInvoice, isPending: isUpdatingInvoice } =
    useUpdateInvoice({
      onSuccess: async ({ data }) => {
        if (form.getValues("files")) {
          const deletedAttachments = invoice.attachments.filter(
            (attachment) =>
              !form
                .watch("files")
                .some((file) => (file as { id: string }).id === attachment.id)
          );

          const newAttachments = form
            .watch("files")
            .filter((file) => file instanceof File);

          // Uploads use allSettled (not Promise.all over fire-and-forget mutate): the
          // invoice was already saved, so a failed attachment must NOT reject
          // onSuccess (that would route to onError, keep the drawer open, and risk a
          // duplicate re-submit). We await all uploads, then surface any failure.
          const attachmentResults = await Promise.allSettled([
            ...newAttachments.map((file) =>
              createInvoiceAttachment({
                file,
                invoiceId: data.value.id,
              })
            ),
            ...(deletedAttachments ?? []).map((attachment) =>
              deleteInvoiceAttachment({
                invoiceId: data.value.id,
                attachmentId: attachment.id,
              })
            ),
          ]);
          if (attachmentResults.some((r) => r.status === "rejected")) {
            console.error(
              "Attachment upload failed",
              attachmentResults.filter((r) => r.status === "rejected")
            );
            toast({
              variant: "destructive",
              title: t("error_message.upload_failed"),
              description: t("error_message.upload_failed_description"),
            });
          }
          refetch();
          setOpen(false);
          form.setValue("vendor", { id: data.value.vendorId });
          queryClient.invalidateQueries({
            queryKey: ["vendors"],
          });
          toast({
            title: t("success_message.title"),
            description: t("success_message.edit_invoice_description"),
          });

          Mixpanel.track("Expense Edited Successfully");
        } else {
          refetch();
          setOpen(false);
          form.setValue("vendor", { id: data.value.vendorId });
          queryClient.invalidateQueries({
            queryKey: ["vendors"],
          });
          toast({
            title: t("success_message.title"),
            description: t("success_message.edit_invoice_description"),
          });

          Mixpanel.track("Expense Edited Successfully");
        }

        isSubmittingRef.current = false;
      },
      onError: (error) => {
        console.error(error);
        Mixpanel.track("Expense Edit Failed");
        isSubmittingRef.current = false;
      },
    });

  const vendors = useMemo(() => vendorsData?.value ?? [], [vendorsData]);

  const defaultVendor = useMemo(() => {
    return vendors.find((vendor) => vendor.id === invoice.vendorId);
  }, [vendors, invoice.vendorId]);

  const defaultValues: Partial<InvoiceFormValues> = useMemo(() => {
    return {
      ...invoice,
      issueDate: invoice?.issueDate ? new Date(invoice.issueDate) : new Date(),
      dueDate: invoice?.dueDate ? new Date(invoice.dueDate) : new Date(),
      files:
        (invoice?.attachments ?? [])
          .map((attachment) => {
            return { id: attachment.id, url: attachment.filePath };
          })
          .filter((file) => !file) || [],
      vendor: defaultVendor ? { id: defaultVendor.id } : null,
      conversionRate: invoice.conversionRate,
      vendorPaymentMethods: [],
      lineItems: (invoice.lineItems ?? []).map((lineItem) => ({
        ...lineItem,
        quantity: lineItem.quantity ?? 0,
        unitPrice: lineItem.unitPrice ?? 0,
        vatRate: lineItem.vatRate ?? 0,
        vatAmount: lineItem.vatAmount ?? 0,
        budgetCategoryId: lineItem.budgetCategoryId ?? undefined,
        budgetSubcategoryId: lineItem.budgetSubcategoryId ?? undefined,
        budgetLineItemId: lineItem.budgetLineItemId ?? undefined,
      })),
    };
  }, [invoice, defaultVendor]);

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues,
  });
  const handleUpdateInvoice = (values: InvoiceFormValues, vendorId: string) => {
    updateInvoice({
      ...values,
      id: invoice.id,
      version: invoice.version ?? 0,
      totalAmount: values.lineItems.reduce(
        (acc, lineItem) =>
          acc +
          ((lineItem.unitPrice ?? 0) * (lineItem.quantity ?? 0) +
            ((lineItem.unitPrice ?? 0) *
              (lineItem.quantity ?? 0) *
              (lineItem.vatRate ?? 0)) /
              100),
        0
      ),
      organizationId: getActiveOrganizationId() ?? user?.org_id ?? "",
      vendorId: vendorId,
      status: values.status,
      conversionRate: values.conversionRate,
      lineItems: values.lineItems.map((lineItem) => ({
        ...lineItem,
        budgetCategoryId: lineItem.budgetCategoryId ?? undefined,
        budgetSubcategoryId: lineItem.budgetSubcategoryId ?? undefined,
        budgetLineItemId: lineItem.budgetLineItemId ?? undefined,
      })),
      budgetId: values.budgetId ?? "",
      issueDate: toDateOnlyString(values.issueDate),
      dueDate: toDateOnlyString(values.dueDate),
    });
  };

  const onSubmit = (values: InvoiceFormValues) => {
    // Block re-entry while a submission is already in flight.
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (typeof values.vendor === "object" && values.vendor !== null) {
      handleUpdateInvoice(values, values.vendor.id);
    } else if (typeof values.vendor === "string") {
      const existingVendor = vendors?.find(
        (vendor) =>
          vendor.name.toLocaleLowerCase() ===
          (values.vendor as string).toLocaleLowerCase()
      );

      if (existingVendor) {
        handleUpdateInvoice(values, existingVendor.id);
      } else {
        createVendor({
          name: values.vendor,
          paymentMethods: (values.vendorPaymentMethods ?? []).map((item) => {
            return {
              ...item,
              name: values.vendor as string,
            };
          }),
        });
      }
    }
  };

  useEffect(() => {
    const getPreviewUrls = async () => {
      const files = invoice.attachments;

      if (!files || files.length === 0) {
        return;
      }

      try {
        const urlResults = await Promise.all(
          files.map(async (file) => {
            const res = await api.get<ApiResponse<string>>(
              `${BASE_URL}/invoices/attachments/${file.id}/url`
            );
            return { id: file.id, url: res.data.value };
          })
        );
        form.setValue("files", urlResults);
      } catch (error) {
        form.setValue("files", []);
        console.error("Error fetching attachment URLs:", error);
      }
    };

    getPreviewUrls();
  }, [invoice.attachments, form, open]);

  useEffect(() => {
    // Set default values
    form.reset({
      ...defaultValues,
    });
  }, [form, defaultValues]);

  return (
    <>
      <InvoiceForm
        title={t("title.edit_invoice")}
        open={open}
        setOpen={setOpen}
        form={form}
        step={step}
        setStep={setStep}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        isSubmitting={isUpdatingInvoice || isCreatingVendor}
        initialVesselId={undefined}
      />
    </>
  );
};

export default EditInvoiceForm;
